import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';

import logo from '../Assets/Images/logoHeader.svg';
import TopMenu from './TopMenu';
import CurrencyBox from './CurrencyBox';
import QuoteHeaderShortcut from './QuoteHeaderShortcut';

import { hasRight } from '../Utils/rights';

/**
 * Header mobile-first: logo à esquerda + hamburger à direita.
 * Drawer reproduz a mesma sequência de elementos do header desktop
 * (esquerda→direita = topo→fundo):
 *
 *   1. Quem somos
 *   2. Dúvidas frequentes
 *   3. CurrencyBox (apenas logado)
 *   4. — sem login:  Login + Cadastre-se
 *      — logado:     Identidade (usuário/agência, SUDO para master)
 *                    TopMenu existente (preserva rights/master) + Logoff
 *
 * Props compatíveis com Layouts/Mobile.js (passadas pelo Layout):
 *  - loginStatus, securedRoute
 *  - currentCompanyStatus, activationStatus
 *  - masterUser, userRights
 *  - agencyName, agencyUser, currentAgency
 *  - handleClickScroll, setDialog, confirm
 *  - setCurrentAgencyAction, resetCurrentAgencyAction
 */
const MobileHeader = (props) => {
    const {
        loginStatus,
        securedRoute,
        currentCompanyStatus,
        activationStatus,
        masterUser,
        userRights,
        agencyName,
        agencyUser,
        currentAgency,
        handleClickScroll,
        setDialog,
        confirm,
        setCurrentAgencyAction,
        resetCurrentAgencyAction,
    } = props;

    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const isAdminRoute = pathname.includes('admin');

    const [open, setOpen] = React.useState(false);

    // Fecha a gaveta automaticamente quando o usuário desloga
    // (logoff via confirmPopup do TopMenu não tem como avisar o
    // MobileHeader diretamente; reagimos à transição de loginStatus).
    React.useEffect(() => {
        if (!loginStatus) setOpen(false);
    }, [loginStatus]);

    // Body class marker: usada por CSS global para
    //  (a) esconder o botão flutuante do WhatsApp
    //  (b) travar scroll do fundo enquanto a gaveta está aberta
    //
    // Padrão position:fixed + top:-scrollY preserva a posição de scroll
    // no iOS Safari, onde overflow:hidden no body sozinho não basta.
    React.useEffect(() => {
        const cls = 'mobile-revamp-drawer-open';
        const body = document.body;

        if (open) {
            const scrollY = window.scrollY || window.pageYOffset || 0;
            body.dataset.mrScrollY = String(scrollY);
            body.style.top = `-${scrollY}px`;
            body.classList.add(cls);
        } else if (body.classList.contains(cls)) {
            const scrollY = parseInt(body.dataset.mrScrollY || '0', 10);
            body.classList.remove(cls);
            body.style.top = '';
            delete body.dataset.mrScrollY;
            window.scrollTo(0, scrollY);
        }

        return () => {
            if (body.classList.contains(cls)) {
                body.classList.remove(cls);
                body.style.top = '';
                delete body.dataset.mrScrollY;
            }
        };
    }, [open]);

    // TopMenu chama op?.current?.hide() ao navegar — entregamos um shim
    // que fecha o Sidebar no lugar do OverlayPanel original.
    const fakeOp = React.useMemo(() => ({
        current: {
            hide:   () => setOpen(false),
            toggle: () => setOpen(o => !o),
        }
    }), []);

    const goPublicSection = (target) => {
        setOpen(false);
        if (securedRoute) {
            handleClickScroll(target);
        } else {
            navigate('/home');
        }
    };

    const openRegister = () => {
        setOpen(false);
        if (securedRoute) setDialog('registerModal', true);
        else navigate('/home');
    };

    const onLogoClick = () => {
        if (loginStatus && currentCompanyStatus === 1 && activationStatus) {
            navigate('/order');
        }
    };

    const canSudo = hasRight(userRights, 'createBookingAsAgency');
    const sudoActive = masterUser && currentAgency && currentAgency.agencyCode;

    return (
        <div className="wrapper__header mobile-revamp-header">
            <div className="mobile-revamp-header__bar">
                <div
                    className="mobile-revamp-header__logo"
                    onClick={onLogoClick}
                    role={loginStatus ? 'button' : undefined}
                >
                    <img src={logo} alt="Dream4" className="noSelect" />
                </div>

                <div className="mobile-revamp-header__actions">
                    {/* Atalho de cotação: só aparece logado E com cotação (variant icon) */}
                    {loginStatus && <QuoteHeaderShortcut variant="icon" />}
                    <Button
                        icon="pi pi-bars"
                        text
                        aria-label="Abrir menu"
                        className="mobile-revamp-header__hamburger"
                        onClick={() => setOpen(true)}
                    />
                </div>
            </div>

            <Sidebar
                visible={open}
                onHide={() => setOpen(false)}
                position="right"
                className="mobile-revamp-sidebar"
                showCloseIcon
                blockScroll
            >
                <div className="mobile-revamp-sidebar__list">
                    {/* Institucional — apenas para visitantes (deslogados).
                        Owner pediu para remover do menu logado pra manter foco
                        no contexto da agência. */}
                    {!loginStatus && (
                        <>
                            <Button
                                text label="Quem somos"
                                className="mobile-revamp-sidebar__item"
                                onClick={() => goPublicSection('memovoSection')}
                            />
                            <Button
                                text label="Dúvidas frequentes"
                                className="mobile-revamp-sidebar__item"
                                onClick={() => goPublicSection('contatoSection')}
                            />
                        </>
                    )}

                    {/* Câmbio — apenas logado, primeiro item da gaveta */}
                    {loginStatus && (
                        <>
                            <div className="mobile-revamp-sidebar__currency-wrapper">
                                <CurrencyBox />
                            </div>
                            {/* Minhas cotações — linha própria (não ao lado do câmbio) */}
                            <div className="mobile-revamp-sidebar__quote-row">
                                <QuoteHeaderShortcut variant="label" />
                            </div>
                        </>
                    )}

                    {/* 4a. Sem login — Login + Cadastre-se */}
                    {!loginStatus && (
                        <>
                            <div className="mobile-revamp-sidebar__divider" />
                            <Button
                                rounded label="Login"
                                className="mobile-revamp-sidebar__item mobile-revamp-sidebar__item--primary"
                                onClick={() => goPublicSection('loginSection')}
                            />
                            <Button
                                text label="Cadastre-se"
                                className="mobile-revamp-sidebar__item mobile-revamp-sidebar__item--pink"
                                onClick={openRegister}
                            />
                        </>
                    )}

                    {/* 4b. Logado — identidade + SUDO + TopMenu */}
                    {loginStatus && (
                        <>
                            <div className="mobile-revamp-sidebar__divider" />

                            <div className="mobile-revamp-sidebar__identity">
                                {agencyUser && (
                                    <div className="mobile-revamp-sidebar__identity-row">
                                        <span className="mobile-revamp-sidebar__identity-label">
                                            {masterUser ? 'Logado como' : 'Usuário'}
                                        </span>
                                        <strong className="mobile-revamp-sidebar__identity-value">
                                            {agencyUser}
                                        </strong>
                                    </div>
                                )}

                                {!masterUser && agencyName && (
                                    <div className="mobile-revamp-sidebar__identity-row">
                                        <span className="mobile-revamp-sidebar__identity-label">Agência</span>
                                        <strong className="mobile-revamp-sidebar__identity-value">{agencyName}</strong>
                                    </div>
                                )}

                                {sudoActive && (
                                    <>
                                        <div className="mobile-revamp-sidebar__identity-row mobile-revamp-sidebar__identity-row--sudo">
                                            <span className="mobile-revamp-sidebar__identity-label">Atuando como</span>
                                            <strong className="mobile-revamp-sidebar__identity-value">
                                                {currentAgency.agencyName}
                                            </strong>
                                            {currentAgency.employeeName && (
                                                <span className="mobile-revamp-sidebar__identity-employee">
                                                    {currentAgency.employeeName}
                                                </span>
                                            )}
                                        </div>
                                        <Button
                                            text
                                            icon="pi pi-times"
                                            label="Sair do modo agência"
                                            className="mobile-revamp-sidebar__item mobile-revamp-sidebar__item--inline"
                                            onClick={() => {
                                                setOpen(false);
                                                resetCurrentAgencyAction?.();
                                            }}
                                        />
                                    </>
                                )}

                                {masterUser && !sudoActive && canSudo && (
                                    <Button
                                        text
                                        icon="pi pi-plus"
                                        label="Selecionar agência"
                                        className="mobile-revamp-sidebar__item mobile-revamp-sidebar__item--inline"
                                        onClick={() => {
                                            setOpen(false);
                                            setCurrentAgencyAction?.();
                                        }}
                                    />
                                )}
                            </div>

                            {/* Admin: botão Buscar hotéis (mantém identidade + menu, como no desktop) */}
                            {isAdminRoute && (
                                <>
                                    <div className="mobile-revamp-sidebar__divider" />
                                    <Button
                                        icon="pi pi-search"
                                        label="Buscar hotéis"
                                        text
                                        className="mobile-revamp-sidebar__item"
                                        onClick={() => { setOpen(false); navigate('/order'); }}
                                    />
                                </>
                            )}

                            {/* Itens do menu (TopMenu) — sempre, no admin e fora dele */}
                            <div className="mobile-revamp-sidebar__divider" />
                            <div className="mobile-revamp-sidebar__list mobile-revamp-sidebar__list--topmenu">
                                <TopMenu op={fakeOp} confirm={confirm} />
                            </div>
                        </>
                    )}
                </div>
            </Sidebar>
        </div>
    );
};

export default MobileHeader;
